import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useForm } from "@inertiajs/react";
import { Building2, Camera, Loader2 } from "lucide-react";
import { useRef } from "react";

type Props = {
  organization: {
    name: string;
    slug: string;
    logo: string | null;
  } | null;
  canEditName: boolean;
  canEditLogo: boolean;
};

export function OrganizationSection({
  organization,
  canEditName,
  canEditLogo,
}: Props) {
  if (!organization) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Organização</CardTitle>
          <CardDescription>Nenhuma organização selecionada.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const canEdit = canEditName || canEditLogo;

  if (!canEdit) {
    return <OrganizationReadOnly organization={organization} />;
  }

  return (
    <OrganizationForm
      organization={organization}
      canEditName={canEditName}
      canEditLogo={canEditLogo}
    />
  );
}

function OrganizationReadOnly({
  organization,
}: {
  organization: NonNullable<Props["organization"]>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Organização</CardTitle>
        <CardDescription>
          Informações da sua organização.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <Avatar className="size-16 rounded-lg">
            <AvatarImage src={organization.logo ?? undefined} />
            <AvatarFallback className="rounded-lg text-lg">
              <Building2 className="size-6 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
          <div className="text-sm">
            <p className="font-medium">{organization.name}</p>
            <p className="text-muted-foreground font-mono text-xs">
              {organization.slug}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Nome</Label>
          <p className="text-sm">{organization.name}</p>
        </div>

        <div className="space-y-2">
          <Label>Identificador (slug)</Label>
          <p className="text-sm font-mono text-muted-foreground">
            {organization.slug}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function OrganizationForm({
  organization,
  canEditName,
  canEditLogo,
}: {
  organization: NonNullable<Props["organization"]>;
  canEditName: boolean;
  canEditLogo: boolean;
}) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const form = useForm({
    section: "organization",
    name: organization.name,
    logo: null as File | null,
  });

  const logoPreview = form.data.logo
    ? URL.createObjectURL(form.data.logo)
    : organization.logo;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.put("/app/settings", {
      forceFormData: true,
    });
  }

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      form.setData("logo", file);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organização</CardTitle>
        <CardDescription>
          Gerencie as informações da sua organização.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="relative group">
              <Avatar className="size-16 rounded-lg">
                <AvatarImage src={logoPreview ?? undefined} />
                <AvatarFallback className="rounded-lg text-lg">
                  <Building2 className="size-6 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
              {canEditLogo && (
                <>
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Camera className="size-4 text-white" />
                  </button>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </>
              )}
            </div>
            <div className="text-sm">
              <p className="font-medium">{organization.name}</p>
              <p className="text-muted-foreground font-mono text-xs">
                {organization.slug}
              </p>
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="org-name">Nome</Label>
            {canEditName ? (
              <Input
                id="org-name"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
              />
            ) : (
              <p className="text-sm">{organization.name}</p>
            )}
            {form.errors.name && (
              <p className="text-destructive text-sm">{form.errors.name}</p>
            )}
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <Label>Identificador (slug)</Label>
            <p className="text-sm font-mono text-muted-foreground">
              {organization.slug}
            </p>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={form.processing}>
              {form.processing && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Salvar alterações
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
